/**
 * 2.0 Today, Part 1 frames 1l and 1m (strings 50 to 60): the Next steps card.
 *
 * One card: what is left and who acts. A row is present only while its step is
 * open, so a done step leaves the card and the card itself leaves when nothing
 * is left. The card computes nothing beyond that: every input is a value the
 * route already holds.
 *
 *   Approve  open while the macro plan status is `proposed`.
 *   Draft    open while undrafted upcoming weeks exist (the caller passes their
 *            label, from the REAL `draftTargetWeeks` + `formatDraftWeeksLabel`
 *            exported by `components/plan/BatchDraftCard.tsx`, null when there
 *            are none) AND `draft_week_journey_v1` is on: string 56 promises
 *            "review each week", which only the journey delivers.
 *   Add      open while no race is on the calendar.
 *
 * Targets are today's: Review and Draft go to the season plan (`/plan`, the
 * entry the current "Draft upcoming weeks" link uses; there the draft CTA
 * re-aims at the review takeover when the journey flag is on); Add opens the
 * meet form at `/races?openMeetForm=1`. The season plan's path is read from
 * `useSeasonPath()` (wave 2 amendment A1): `/plan` with `train_2_0_v1` off,
 * `/plan/season` with it on, where `/plan` becomes This week.
 *
 * Add is an ATHLETE door (UX review 2026-10-18, defect 1): with `races_2_0_v1` on it goes to
 * `/races?openRaceForm=1&priority=goal`, which opens the Upcoming sheet for every account with Priority on
 * "Goal race" (W2-UX-FIX2; the other race doors keep the sheet's default). `openMeetForm` is the coach's
 * door there (a dual-role account gets the team-meet sheet), so an athlete's goal race must not use it.
 *
 * Opt-in by construction: nothing imports this until W1-TODAY-WIRE mounts it
 * behind `today_2_0_v1`. It carries no outer margin; the mount supplies the gutter.
 */
import { useEffect, useId } from 'react';
import { Link } from 'react-router-dom';
import type { MacroPlanStatus } from '../../../hooks/useMacroPlan';
import { useServerFlag } from '../../../hooks/useServerFlag';
import { useSeasonPath } from '../../../lib/train/routes';
import { preloadRacesRoute } from '../../RouteFrame/preloadRaces';
import styles from './NextStepsCard.module.css';

export interface NextStepsCardProps {
  /** `useMacroPlan().status` as `TodayRealData` holds it. */
  approvalStatus: MacroPlanStatus;
  /** The weeks the next draft would author, as `formatDraftWeeksLabel` prints
   *  them ("weeks 7 to 10"); null when no upcoming week is undrafted. */
  draftWeeksLabel: string | null;
  /** `draft_week_journey_v1`. */
  draftJourneyOn: boolean;
  /** A race is on the calendar. */
  hasGoalRace: boolean;
}

interface Step {
  key: 'approve' | 'draft' | 'add';
  title: string;
  who: string;
  action: string;
  to: string;
}

export function NextStepsCard({
  approvalStatus,
  draftWeeksLabel,
  draftJourneyOn,
  hasGoalRace,
}: NextStepsCardProps) {
  const titleId = useId();
  // Wave 2 amendment A1 (TC-3): the season plan's route comes from `useSeasonPath()`, `/plan` with
  // `train_2_0_v1` off (wave 1 behavior, unchanged) and `/plan/season` with it on. Above every early return.
  const seasonPath = useSeasonPath();
  // UX defect 1: the Add door is the athlete's (the Upcoming sheet) once `races_2_0_v1` is on. Above every early return.
  const racesV2 = useServerFlag('races_2_0_v1');
  // W2-UX-FIX2 (defect 3): while the Add door is on the card, start loading the Races chunk so the first tap does
  // not wait on it. Flag on only; a script request, never a call to the API. Above every early return.
  const addDoorOpen = racesV2 && !hasGoalRace;
  useEffect(() => {
    if (addDoorOpen) preloadRacesRoute();
  }, [addDoorOpen]);
  const steps: Step[] = [];

  if (approvalStatus === 'proposed') {
    steps.push({
      key: 'approve',
      title: 'Approve your season plan',
      who: 'Only you can approve it. It records your choice, and you can still ask for a new plan.',
      action: 'Review',
      to: seasonPath,
    });
  }

  const draftLabel = draftWeeksLabel?.trim() ?? '';
  if (draftJourneyOn && draftLabel !== '') {
    steps.push({
      key: 'draft',
      title: `Draft ${draftLabel}`,
      who: 'You draft them, then review each week before anything is added.',
      action: 'Draft',
      to: seasonPath,
    });
  }

  if (!hasGoalRace) {
    steps.push({
      key: 'add',
      title: 'Add your goal race',
      who: 'You add it. Your plan sharpens around it.',
      action: 'Add',
      // W2-UX-FIX2: `priority=goal` opens the sheet with Priority on "Goal race" (the Races route reads it).
      to: racesV2 ? '/races?openRaceForm=1&priority=goal' : '/races?openMeetForm=1',
    });
  }

  if (steps.length === 0) return null;

  return (
    <section className={styles.card} aria-labelledby={titleId}>
      <div className={styles.head}>
        <h2 id={titleId} className={styles.title}>
          Next steps
        </h2>
        <span className={styles.count}>{`${steps.length} left`}</span>
      </div>
      {/* role="list": Safari drops list semantics from a list-style: none list. */}
      <ul className={styles.list} role="list">
        {steps.map((step) => (
          <li key={step.key} className={styles.row} data-step={step.key}>
            <div className={styles.copy}>
              <span className={styles.stepTitle}>{step.title}</span>
              <span className={styles.who}>{step.who}</span>
            </div>
            <Link to={step.to} className={styles.action}>
              {step.action}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
